import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import Box from '@mui/material/Box'
import { People, Search, Refresh } from '@mui/icons-material'
import { TextField, InputAdornment, FormControl, Select, MenuItem, IconButton, Chip, ToggleButton, ToggleButtonGroup, Autocomplete, Checkbox as MuiCheckbox } from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'

// Third-party Imports
import { rankItem } from '@tanstack/match-sorter-utils'

// Component Imports
import FirmContactTable from '../organisms/FirmContactTable'
import CampaignProgressBar from '../../../../../campaigns/components/CampaignProgressBar'

import { getCampaignFirmsAndState, getCampaign, updateContactSelection, approveContactSelection } from '../../../services/B2BServices'
import { getUrlParams } from '../../../services/urlServices'
import { useToast } from '../../../../../campaigns/hooks/useToast'
import { useGlobalStoreActions } from '../../../../../store/globalStore'

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)

  addMeta({
    itemRank
  })

  return itemRank.passed
}

const ContactSelection = ({ onCampaignUpdate, campaign, isSearching = false }) => {
  const { t } = useTranslation()
  const queryParams = getUrlParams()

  const { showSuccess, showError } = useToast()
  const { setCampaignStatus } = useGlobalStoreActions()

  const [nextLoading, setNextLoading] = useState(false)

  const [firmsWithContacts, setFirmsWithContacts] = useState([])
  const [fetching, setFetching] = useState(true)
  const [contactSelection, setContactSelection] = useState({})
  const [campaignData, setCampaignData] = useState(null)

  const intervalRef = useRef(null)

  const [searchTerm, setSearchTerm] = useState('')
  const [pageSize, setPageSize] = useState(10)

  const [selectedFilter, setSelectedFilter] = useState('selected')
  const [suitabilityFilter, setSuitabilityFilter] = useState([])

  const suitabilityOptions = useMemo(() => [
    { value: 1, label: t('mailTemplate.suitabilityLevel.highlySuitable'), key: 'HIGHLY_SUITABLE' },
    { value: 2, label: t('mailTemplate.suitabilityLevel.moderatelySuitable'), key: 'MODERATELY_SUITABLE' },
    { value: 3, label: t('mailTemplate.suitabilityLevel.minimallySuitable'), key: 'MINIMALLY_SUITABLE' },
    { value: 4, label: t('mailTemplate.suitabilityLevel.notSuitable'), key: 'NOT_SUITABLE' },
    { value: 5, label: t('mailTemplate.suitabilityLevel.cannotAccess'), key: 'CANNOT_ACCESS' }
  ], [t])

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [saveSelectedLoading, setSaveSelectedLoading] = useState(false)
  const [originalSelection, setOriginalSelection] = useState({})

  const isAnyContactSelected = useMemo(() => {
    return Object.keys(contactSelection).some(key => contactSelection[key])
  }, [contactSelection])

  const filteredFirms = useMemo(() => {
    let filtered = firmsWithContacts

    if (selectedFilter === 'selected') {
      filtered = filtered.filter(firm => firm.selected === true)
    } else if (selectedFilter === 'not-selected') {
      filtered = filtered.filter(firm => firm.selected === false)
    }

    if (suitabilityFilter.length > 0) {
      const selectedKeys = suitabilityFilter.map(opt => opt.key)
      filtered = filtered.filter(firm => selectedKeys.includes(firm.suitabilityLevel))
    }

    return filtered
  }, [firmsWithContacts, selectedFilter, suitabilityFilter, contactSelection])

  const totalSelectedCount = useMemo(() => {
    return Object.keys(contactSelection).filter(key => contactSelection[key]).length
  }, [contactSelection])

  const hasSelectionChanges = useMemo(() => {
    const currentKeys = Object.keys(contactSelection).filter(key => contactSelection[key])
    const originalKeys = Object.keys(originalSelection).filter(key => originalSelection[key])

    if (currentKeys.length !== originalKeys.length) {
      return true
    }

    return currentKeys.some(key => !originalSelection[key]) || originalKeys.some(key => !contactSelection[key])
  }, [contactSelection, originalSelection])

  const collectSelectionData = useCallback(() => {
    const campaignFirmContacts = []

    firmsWithContacts.forEach(firm => {
      const selectedContactIds = []
      ;(firm.contacts || []).forEach(contact => {
        const key = `${firm.identifier}_${contact.id}`
        if (contactSelection[key]) {
          selectedContactIds.push(contact.id)
        }
      })
      campaignFirmContacts.push({
        identifier: firm.identifier,
        selectedContactIds
      })
    })

    return campaignFirmContacts
  }, [firmsWithContacts, contactSelection])

  const handleSaveSelected = useCallback(async () => {
    setSaveSelectedLoading(true)

    try {
      const campaignFirmContacts = collectSelectionData()

      const payload = {
        campaignId: queryParams.b2bCampaignId,
        campaignFirmContacts
      }

      await updateContactSelection(payload)

      setOriginalSelection({ ...contactSelection })
      setHasUnsavedChanges(false)

      showSuccess(t('mailTemplate.employeeSelection.saveSelectedSuccess'))
    } catch (error) {
      console.error('Failed to save selected contacts:', error)
      showError(t('mailTemplate.employeeSelection.saveSelectedError'))
    } finally {
      setSaveSelectedLoading(false)
    }
  }, [collectSelectionData, queryParams.b2bCampaignId, contactSelection, showSuccess, showError, t])

  const onSubmit = useCallback(async () => {
    setNextLoading(true)

    if (!isAnyContactSelected) {
      setNextLoading(false)
      showError(t('mailTemplate.employeeSelection.noEmployeesSelectedError'))
      return
    }

    try {
      await approveContactSelection(queryParams.b2bCampaignId)

      showSuccess(t('mailTemplate.employeeSelection.approveSelectionSuccess'))

      if (onCampaignUpdate) {
        await onCampaignUpdate()
      }
    } catch (error) {
      console.error('Failed to submit campaign:', error)
      showError(t('mailTemplate.employeeSelection.approveSelectionError'))
    } finally {
      setNextLoading(false)
    }
  }, [isAnyContactSelected, queryParams.b2bCampaignId, showSuccess, showError, t, onCampaignUpdate])

  const handleRefresh = useCallback(() => {
    if (queryParams?.b2bCampaignId) {
      setFetching(true)

      const minDelayPromise = new Promise(resolve => setTimeout(resolve, 800))
      const apiPromise = getCampaignFirmsAndState({ b2bCampaignId: queryParams.b2bCampaignId })

      Promise.all([apiPromise, minDelayPromise]).then(([res]) => {
        const firms = res.data.campaignFirmList || []
        setFirmsWithContacts(firms)

        if (res.data.campaignStatus) {
          setCampaignData(prev => ({ ...prev, status: res.data.campaignStatus }))
          setCampaignStatus(queryParams.b2bCampaignId, res.data.campaignStatus)
        }

        const backendSelection = {}
        firms.forEach(firm => {
          ;(firm.contacts || []).forEach(contact => {
            if (contact.selected === true) {
              backendSelection[`${firm.identifier}_${contact.id}`] = true
            }
          })
        })

        setContactSelection(backendSelection)
        setOriginalSelection(backendSelection)
      }).finally(() => {
        setFetching(false)
      })
    }
  }, [queryParams?.b2bCampaignId, setCampaignStatus])

  const queryParamsError = useCallback((qParams, b2bAccountIdError, b2bCampaignIdError) => {
    const b2bAccountId = qParams?.b2bAccountId
    const b2bCampaignId = qParams?.b2bCampaignId

    if (b2bAccountId && b2bCampaignId) {
      return false
    } else if (b2bCampaignId) {
      return <div>{b2bAccountIdError}</div>
    } else {
      return <div>{b2bCampaignIdError}</div>
    }
  }, [])

  useEffect(() => {
    if (queryParams?.b2bCampaignId) {
      getCampaign(queryParams.b2bCampaignId).then((campaignRes) => {
        setCampaignData(campaignRes.data)
        if (campaignRes.data?.status) {
          setCampaignStatus(queryParams.b2bCampaignId, campaignRes.data.status)
        }
      }).catch((error) => {
        console.error('Failed to fetch campaign data:', error)
      })
    }
  }, [queryParams.b2bCampaignId, setCampaignStatus])

  useEffect(() => {
    if (queryParams?.b2bCampaignId) {
      setFetching(true)
      getCampaignFirmsAndState({ b2bCampaignId: queryParams.b2bCampaignId }).then((res) => {
        const firms = res.data.campaignFirmList || []
        setFirmsWithContacts(firms)

        const backendSelection = {}
        firms.forEach(firm => {
          ;(firm.contacts || []).forEach(contact => {
            if (contact.selected === true) {
              backendSelection[`${firm.identifier}_${contact.id}`] = true
            }
          })
        })

        setContactSelection(backendSelection)
        setOriginalSelection(backendSelection)
      }).finally(() => {
        setFetching(false)
      })
    }
  }, [queryParams.b2bCampaignId])

  useEffect(() => {
    setHasUnsavedChanges(hasSelectionChanges)
  }, [hasSelectionChanges])

  useEffect(() => {
    if (isSearching && !fetching) {
      intervalRef.current = setInterval(() => {
        handleRefresh()
      }, 7500)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isSearching, fetching, handleRefresh])

  return (
    queryParamsError(queryParams, t('mailTemplate.wizard.b2bAccountIdError'), t('mailTemplate.wizard.b2bCampaignIdError')) ||
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="p-2" style={{ direction: 'ltr' }}>
        <div className="flex justify-center items-center mb-4">
          <div className="flex flex-col items-center">
            <People sx={{ fontSize: 50 }} color="primary"/>
            <Typography variant="caption" color="primary">
              {t('mailTemplate.employeeSelection.title')}
            </Typography>
          </div>
        </div>

        {!isSearching && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Chip
              label={`${totalSelectedCount} ${t('mailTemplate.employeeSelection.employeesSelected')}`}
              color={totalSelectedCount > 0 ? 'primary' : 'default'}
              size="medium"
            />

            <div className="flex gap-2">
              <Button
                variant="outlined"
                onClick={handleSaveSelected}
                disabled={saveSelectedLoading || !hasUnsavedChanges}
                startIcon={saveSelectedLoading ? <CircularProgress sx={{ color: 'inherit' }} size={18}/> : null}
                sx={{ borderRadius: 2 }}
              >
                {saveSelectedLoading ? t('mailTemplate.employeeSelection.savingSelected') : t('mailTemplate.employeeSelection.saveSelected')}
              </Button>
              <Tooltip
                title={hasUnsavedChanges ? t('mailTemplate.employeeSelection.saveBeforeStart') : ''}
                arrow
                placement="top"
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={nextLoading || hasUnsavedChanges}
                    startIcon={nextLoading ? <CircularProgress sx={{ color: 'inherit' }} size={18}/> : null}
                    sx={{ borderRadius: 2 }}
                  >
                    {nextLoading ? t('mailTemplate.employeeSelection.approvingSelection') : t('mailTemplate.employeeSelection.approveSelection')}
                  </Button>
                </span>
              </Tooltip>
            </div>
          </Box>
        )}
      </div>

      <Box sx={{ px: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={handleRefresh}
            disabled={fetching}
            color="primary"
            sx={{ borderRadius: 2 }}
          >
            {fetching ? <CircularProgress size={24}/> : <Refresh/>}
          </IconButton>
        </Box>

        <Box sx={{
          display: 'flex',
          gap: 5,
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <ToggleButtonGroup
            value={selectedFilter}
            exclusive
            onChange={(_, newValue) => newValue && setSelectedFilter(newValue)}
            size="large"
            sx={{
              '& .MuiToggleButton-root': {
                py: 1,
                px: 2.5,
                fontSize: '14px',
                minWidth: 'auto',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark'
                  }
                }
              }
            }}
          >
            <ToggleButton value="all">
              {t('mailTemplate.campaignReview.all')}
            </ToggleButton>
            <ToggleButton value="selected">
              {t('mailTemplate.campaignReview.selected')}
            </ToggleButton>
            <ToggleButton value="not-selected">
              {t('mailTemplate.campaignReview.notSelected')}
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Autocomplete
              multiple
              size="small"
              limitTags={2}
              options={suitabilityOptions}
              disableCloseOnSelect
              openOnFocus
              getOptionLabel={(option) => option.label}
              value={suitabilityFilter}
              onChange={(_, newValue) => setSuitabilityFilter(newValue)}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <MuiCheckbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={suitabilityFilter.length === 0 ? t('mailTemplate.campaignPreparation.filter.suitabilityPlaceholder') : ''}
                />
              )}
              slotProps={{
                chip: {
                  clickable: true
                }
              }}
              sx={{
                width: 280,
                '& .MuiAutocomplete-inputRoot': {
                  flexWrap: 'nowrap',
                  overflow: 'hidden',
                  cursor: 'pointer'
                },
                '& .MuiAutocomplete-input': {
                  minWidth: '30px !important'
                },
                '& .MuiAutocomplete-tag': {
                  maxWidth: 100
                }
              }}
            />
            <TextField
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('mailTemplate.employeeSelection.search')}
              size="small"
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search/>
                  </InputAdornment>
                )
              }}
            />
            <FormControl sx={{ minWidth: 80 }}>
              <Select
                value={pageSize}
                onChange={(e) => setPageSize(e.target.value)}
                size="small"
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {isSearching && (
        <CampaignProgressBar
          campaign={campaignData || campaign}
          firms={firmsWithContacts}
        />
      )}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', marginTop: isSearching ? '10px' : 0 }}>
        <FirmContactTable
          data={filteredFirms}
          contactSelection={contactSelection}
          setContactSelection={setContactSelection}
          fetching={fetching}
          fuzzyFilter={fuzzyFilter}
          globalFilter={searchTerm}
          setGlobalFilter={setSearchTerm}
          pageSize={pageSize}
          disableInteraction={false}
          isSearching={isSearching}
        />
      </Box>
    </Box>
  )
}

export default ContactSelection
